/**
 * What a page looks like to the rail, and how the rail groups a list of them.
 *
 * The list itself is not here. It lives in `src/routes/-nav.ts`, which carries
 * no code owner, so a builder adds a page without waiting on a review. This
 * module is framework: it is owned, and it holds only the shape and the two
 * pure functions the rail reads it with.
 */
export interface InternalPage {
  id: string
  path: string
  title: string
  /**
   * Groups this page under a named, collapsible section of the rail —
   * "CS Tools", say — instead of rendering it as a flat row. Absent means a
   * flat row, same as every page before this field existed. Two pages
   * sharing a group name render under one shared disclosure, in the order
   * they appear in this list.
   */
  group?: string
}

/** One named section of the rail: a group's title, and the pages under it, in declared order. */
export interface InternalNavSection {
  group: string
  pages: InternalPage[]
}

/**
 * Split the declared page list into the rows a nav renders flat and the
 * sections it renders as a collapsible disclosure.
 *
 * Grouped and returned in one pass, in declaration order, rather than with a
 * second sort afterwards — `pages.ts` declares the whole rail's order
 * already, and a nav that regrouped it would only need to undo a sort this
 * function never did.
 *
 * @param pages - The declared page list, or a subset of it in tests.
 * @returns The flat rows, and the grouped sections, both in declared order.
 */
export function groupInternalPages(pages: readonly InternalPage[]): {
  ungrouped: InternalPage[]
  sections: InternalNavSection[]
} {
  const ungrouped: InternalPage[] = []
  const sections: InternalNavSection[] = []
  const byGroup = new Map<string, InternalPage[]>()

  for (const page of pages) {
    if (!page.group) {
      ungrouped.push(page)
      continue
    }
    const existing = byGroup.get(page.group)
    if (existing) {
      existing.push(page)
      continue
    }
    const created = [page]
    byGroup.set(page.group, created)
    sections.push({ group: page.group, pages: created })
  }

  return { ungrouped, sections }
}

/**
 * Compare two route paths without tripping over a trailing slash or a query.
 *
 * The rail marks the active row with this, so `/payments/ach-returns/` and
 * `/payments/ach-returns?window=7` must both match the declared path.
 *
 * @param pathname - The path to normalize.
 * @returns The path without its query string or trailing slash.
 */
export function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]

  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}
